import moment, { Moment } from 'moment';

import { IMyDate, IMySingleDateModel } from '../common/types/mydatepicker.stub';

/** API date string: `YYYY-MM-DD` */
export type TanzeelApiDateString = string;

export interface TanzeelDateParts {
  year: number;
  month: number;
  day: number;
}

export type TanzeelDateInput =
  | TanzeelApiDateString
  | Date
  | Moment
  | IMySingleDateModel
  | null
  | undefined;

/**
 * Shared date parsing/formatting for Tanzeel APIs.
 * All persisted dates use `YYYY-MM-DD`.
 */
export class TanzeelDateUtil {
  static readonly API_FORMAT = 'YYYY-MM-DD';

  static isApiDateString(value: unknown): value is TanzeelApiDateString {
    return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  }

  static toMoment(value: TanzeelDateInput): Moment | null {
    if (value == null) {
      return null;
    }

    if (moment.isMoment(value)) {
      return value.clone();
    }

    if (value instanceof Date) {
      const mom = moment(value);
      return mom.isValid() ? mom : null;
    }

    if (typeof value === 'string') {
      const mom = moment(value, TanzeelDateUtil.API_FORMAT, true);
      return mom.isValid() ? mom : null;
    }

    if (value.jsDate instanceof Date) {
      const mom = moment(value.jsDate);
      return mom.isValid() ? mom : null;
    }

    if (value.date != null) {
      const { year, month, day } = value.date;
      const mom = moment({ year, month: month - 1, day });
      return mom.isValid() ? mom : null;
    }

    if (value.formatted) {
      return TanzeelDateUtil.toMoment(value.formatted);
    }

    return null;
  }

  static toDate(value: TanzeelDateInput): Date | null {
    const mom = TanzeelDateUtil.toMoment(value);
    return mom ? mom.toDate() : null;
  }

  static formatApi(value: TanzeelDateInput): TanzeelApiDateString | null {
    const mom = TanzeelDateUtil.toMoment(value);
    return mom ? mom.format(TanzeelDateUtil.API_FORMAT) : null;
  }

  static toDateParts(value: TanzeelDateInput): TanzeelDateParts | null {
    const mom = TanzeelDateUtil.toMoment(value);
    if (!mom) {
      return null;
    }
    return {
      year: mom.year(),
      month: mom.month() + 1,
      day: mom.date(),
    };
  }

  static fromDateParts(parts: TanzeelDateParts): TanzeelApiDateString {
    const mom = moment({
      year: parts.year,
      month: parts.month - 1,
      day: parts.day,
    });
    return mom.format(TanzeelDateUtil.API_FORMAT);
  }

  /** Legacy `{ date: { year, month, day } }` shape used by angular-mydatepicker. */
  static toLegacyDateParts(value: TanzeelDateInput): { date: IMyDate } | null {
    const parts = TanzeelDateUtil.toDateParts(value);
    if (!parts) {
      return null;
    }
    return { date: parts };
  }

  /** @deprecated Prefer `formatApi` — kept for staff screens still on IMySingleDateModel. */
  static toLegacyModel(value: TanzeelDateInput): IMySingleDateModel | null {
    const mom = TanzeelDateUtil.toMoment(value);
    if (!mom) {
      return null;
    }
    const parts = TanzeelDateUtil.toDateParts(mom)!;
    return {
      date: parts,
      formatted: mom.format(TanzeelDateUtil.API_FORMAT),
      jsDate: mom.toDate(),
      epoc: mom.unix(),
    };
  }
}
